import { type FC } from 'react';

import {
  Dropdown,
  DropdownList,
  DropdownListItem,
  DropdownMoreButton,
  Tooltip,
  TooltipBody,
} from '@wk/components-react16';

import type { CheckpointSystemStatus } from '@/types';

import type {
  SiteCheckpointRow,
  SiteDateEditKind,
} from './SiteCheckpointSettingsPage.types';
import { isTransitionAllowed } from './SiteCheckpointSettingsPage.utils';

interface SiteCheckpointSettingsActionsProps {
  row: SiteCheckpointRow | null | undefined;
  context: 'grid' | 'card';
  canManageCheckpoints: boolean;
  isMobileViewport: boolean;
  mutationTooltip?: string | null;
  onOpenMoveModal: (
    row: SiteCheckpointRow,
    targetStatus: CheckpointSystemStatus,
  ) => void;
  onOpenEditDateModal: (row: SiteCheckpointRow, kind: SiteDateEditKind) => void;
}

const SiteCheckpointSettingsActions: FC<SiteCheckpointSettingsActionsProps> = ({
  row,
  context,
  canManageCheckpoints,
  isMobileViewport,
  mutationTooltip,
  onOpenMoveModal,
  onOpenEditDateModal,
}) => {
  if (!row) return null;

  const rowKey = `${row.id}-${row.version ?? ''}-${context}`.replace(
    /[^a-zA-Z0-9_-]/g,
    '-',
  );
  const buttonId = `site-checkpoint-actions-${rowKey}`;
  const tooltipId = `${buttonId}-tooltip`;

  if (!canManageCheckpoints) {
    return (
      <div
        id={tooltipId}
        className="checkpoints-actions-dropdown inline-flex"
        title={mutationTooltip ?? undefined}
      >
        <DropdownMoreButton
          mode="blue"
          buttonId={buttonId}
          isDisabled
        />
        {mutationTooltip ? (
          <Tooltip position="left" targetSelector={`#${tooltipId}`}>
            <TooltipBody slot="tooltipBody">{mutationTooltip}</TooltipBody>
          </Tooltip>
        ) : null}
      </div>
    );
  }

  return (
    <div className="checkpoints-actions-dropdown">
      <DropdownMoreButton mode="blue" buttonId={buttonId} />
      <Dropdown
        targetSelector={`#${buttonId}`}
        menuMode="application"
        srLabelledby={buttonId}
        fullscreenBreakpoint={isMobileViewport ? 'S' : 'XS'}
        dropdownWidth={isMobileViewport ? undefined : '18rem'}
        placementOptions={
          isMobileViewport
            ? undefined
            : {
                positions: [
                  'bottom-left',
                  'top-left',
                  'bottom-right',
                  'top-right',
                ],
              }
        }
      >
        <DropdownList menuMode="application">
          {row.status === 'PREVIEW' ? (
            <DropdownListItem
              onItemClick={() => onOpenEditDateModal(row, 'PREVIEW_END')}
              slot="dropdownListItem"
            >
              Change Out of Preview Date
            </DropdownListItem>
          ) : null}

          {row.status === 'MAINTENANCE' ? (
            <DropdownListItem
              onItemClick={() => onOpenEditDateModal(row, 'MAINTENANCE_DISABLE')}
              slot="dropdownListItem"
            >
              Change Disable Date
            </DropdownListItem>
          ) : null}

          {isTransitionAllowed(row.status, 'LIVE') ? (
            <DropdownListItem
              onItemClick={() => onOpenMoveModal(row, 'LIVE')}
              slot="dropdownListItem"
            >
              Move to Live
            </DropdownListItem>
          ) : null}

          {isTransitionAllowed(row.status, 'PREVIEW') ? (
            <DropdownListItem
              onItemClick={() => onOpenMoveModal(row, 'PREVIEW')}
              slot="dropdownListItem"
            >
              Move to Preview
            </DropdownListItem>
          ) : null}

          {isTransitionAllowed(row.status, 'MAINTENANCE') ? (
            <DropdownListItem
              onItemClick={() => onOpenMoveModal(row, 'MAINTENANCE')}
              slot="dropdownListItem"
            >
              Move to Maintenance
            </DropdownListItem>
          ) : null}

          {isTransitionAllowed(row.status, 'DISABLED') ? (
            <DropdownListItem
              onItemClick={() => onOpenMoveModal(row, 'DISABLED')}
              slot="dropdownListItem"
            >
              Disable
            </DropdownListItem>
          ) : null}
        </DropdownList>
      </Dropdown>
    </div>
  );
};

export default SiteCheckpointSettingsActions;
